import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ArchivePartnerResult,
  CreateLinkInput,
  CreatePartnerInput,
  CreatedLink,
  InvitePartnerMemberInput,
  InvitePartnerMemberResult,
  PartnerWriteResult,
  ProgrammeSettings,
  UpdatePartnerInput,
  UpdateSettingsInput,
  WorkosOrganizationsResponse,
} from '@sparkery/contracts';
import { apiDelete, apiGet, apiPatch, apiPost } from './api.ts';

/**
 * The partner administration writes, from the browser's side.
 *
 * Every one of these invalidates the partner queries rather than patching the cache.
 * The figures on these screens — link counts, terms, archive state — are read by two
 * screens at once, and a hand-patched cache is how one of them starts showing
 * something the server does not agree with.
 *
 * The server's message is surfaced verbatim on failure. A 409 naming the partner who
 * already holds a link tag is the whole point of the response.
 */

/** Both partner screens, since a write on either changes what the other shows. */
function invalidatePartners(queryClient: ReturnType<typeof useQueryClient>, partnerId?: string) {
  void queryClient.invalidateQueries({ queryKey: ['partners'] });
  if (partnerId) {
    void queryClient.invalidateQueries({ queryKey: ['partner-detail', partnerId] });
  }
}

export function useCreatePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePartnerInput) => apiPost<PartnerWriteResult>('/partners', input),
    onSuccess: () => invalidatePartners(queryClient),
  });
}

export function useAssumePartner() {
  return useMutation({
    mutationFn: (partnerId: string) =>
      apiPost<{ sessionId: string; partnerId: string; targetUserId: string; expiresAt: string }>(
        '/impersonations',
        { partnerId, reason: 'Partner list view-as action' },
      ),
  });
}

export function useWorkosOrganizations() {
  return useQuery({
    queryKey: ['workos-organizations'],
    queryFn: ({ signal }) =>
      apiGet<WorkosOrganizationsResponse>('/partners/workos-organizations', signal),
  });
}

export function useUpdatePartner(partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdatePartnerInput) =>
      apiPatch<PartnerWriteResult>(`/partners/${partnerId}`, input),
    onSuccess: () => invalidatePartners(queryClient, partnerId),
  });
}

export function useArchivePartner(partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // No body: archiving takes no arguments, and one that took a confirmation flag
    // would put the safeguard in the request rather than in front of the person.
    mutationFn: () => apiPost<ArchivePartnerResult>(`/partners/${partnerId}/archive`, {}),
    onSuccess: () => invalidatePartners(queryClient, partnerId),
  });
}

export function useCreateLink(partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLinkInput) =>
      apiPost<CreatedLink>(`/partners/${partnerId}/links`, input),
    onSuccess: () => invalidatePartners(queryClient, partnerId),
  });
}

export function useDeleteLink(partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (linkId: string) =>
      apiDelete<{ removed: boolean }>(`/partners/${partnerId}/links/${linkId}`),
    onSuccess: () => invalidatePartners(queryClient, partnerId),
  });
}

export function useInvitePartnerMember(partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InvitePartnerMemberInput) =>
      apiPost<InvitePartnerMemberResult>(`/partners/${partnerId}/team/invitations`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partner-team', partnerId] });
    },
  });
}

// ---------------------------------------------------------------- settings

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: ({ signal }) => apiGet<ProgrammeSettings>('/settings', signal),
  });
}

export type SettingsWriteResult = ProgrammeSettings & { changed: string[] };

export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSettingsInput) => apiPatch<SettingsWriteResult>('/settings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Refer previews a link before it is saved, using the campaign and the default
      // channel — so a settings change means that preview is now stale.
      void queryClient.invalidateQueries({ queryKey: ['refer'] });
    },
  });
}
